/* eslint-disable */
import {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getOnboardingQuestions} from '../../services/onboarding-service';
import {adaptQuestionsResponse} from '../../services/onboarding-adapter';
import {
  DEFAULT_ONBOARDING_CONFIG,
  currencySymbol,
} from '../../config/onboardingData';

const Ctx = createContext(null);

// Use the API value when present; fall back per section so a partial or
// empty response can never crash a screen.
const nonEmpty = (arr, fallback) => (arr && arr.length ? arr : fallback);

const mergeConfig = (base, data = {}) => ({
  ...base,
  ...data,
  welcome: {
    ...base.welcome,
    ...data.welcome,
    stats: {...base.welcome.stats, ...data.welcome?.stats},
  },
  fuelFrequency: {
    options: nonEmpty(data.fuelFrequency?.options, base.fuelFrequency.options),
  },
  fuelType: {
    options: nonEmpty(data.fuelType?.options, base.fuelType.options),
  },
  location: {
    ...base.location,
    ...data.location,
    preview: {
      ...base.location.preview,
      ...data.location?.preview,
      stations: nonEmpty(
        data.location?.preview?.stations,
        base.location.preview.stations,
      ),
    },
  },
});

// Savings per fill-up for a fuel: the API's savingsPerFillUp, or
// (station price - Pompi price) x fill-up size when that is missing.
const savingsPerFill = price => {
  if (!price) return null;
  if (price.savingsPerFillUp != null) return price.savingsPerFillUp;
  if (price.stationPrice != null && price.pompiPrice != null && price.sizePerFillUp != null) {
    return (price.stationPrice - price.pompiPrice) * price.sizePerFillUp;
  }
  return null;
};

export const OnboardingProvider = ({children}) => {
  const {i18n} = useTranslation();
  const [config, setConfig] = useState(DEFAULT_ONBOARDING_CONFIG);
  const [loading, setLoading] = useState(true);

  // null = "user hasn't chosen yet", so the API's default option is used.
  const [answers, setAnswers] = useState({
    frequency: null, // step 2
    fuelType: null, // step 1
    multipleVehicles: false,
    location: null, // step 3
  });
  const update = patch => setAnswers(prev => ({...prev, ...patch}));

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const data = await getOnboardingQuestions();
        if (active) {
          setConfig(mergeConfig(DEFAULT_ONBOARDING_CONFIG, adaptQuestionsResponse(data)));
          console.log('[onboarding] questions loaded from API');
        }
      } catch (e) {
        console.log('[onboarding] API failed, using fallback data:', e?.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [i18n.language]);

  // The chosen fuel, or the API's `default: true` option, or the first one.
  const fuelOptions = config.fuelType.options;
  const selectedFuel =
    fuelOptions.find(o => o.id === answers.fuelType) ||
    fuelOptions.find(o => o.default) ||
    fuelOptions[0];

  // Yearly savings = savings per fill-up (of the chosen fuel)
  //                  x fill-ups per month x 12.
  // Computed here, so every screen that reads yearlySavings / forecast shows
  // the numbers for the fuel picked in step 1. Without API prices (fallback
  // data) the original numbers are kept.
  const perFill = savingsPerFill(config.fuelPrices?.[selectedFuel.id]);
  const freqOptions = useMemo(
    () =>
      config.fuelFrequency.options.map(o => {
        if (perFill == null || !o.fillsPerMonth) return o;
        // monthly = savings per fill-up x fill-ups per month (kept exact),
        // yearly = monthly x 12 (whole euros, for display)
        const monthly = Math.round(perFill * o.fillsPerMonth * 100) / 100;
        const yearly = Math.round(monthly * 12);
        return {
          ...o,
          monthlySavings: monthly,
          yearlySavings: yearly,
          forecast: {min: yearly, max: yearly},
        };
      }),
    [config.fuelFrequency.options, perFill],
  );

  const selectedFrequency =
    freqOptions.find(o => o.id === answers.frequency) ||
    freqOptions.find(o => o.default) ||
    freqOptions[0];

  const value = {
    answers,
    update,
    config: {...config, fuelFrequency: {options: freqOptions}},
    loading,
    currency: currencySymbol(config.currency), // 'EUR' -> '€'
    selectedFrequency,
    selectedFuel,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useOnboarding = () => useContext(Ctx);